<script setup>
import { router, usePage } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const page = usePage()
const { t } = useI18n()

// System-health alerts arrive via the shared `systemAlerts` Inertia prop.
// Deployment alerts surface as a bottom-right toast (handled by the parent
// layout), so they're filtered out of the bell here.
const bellAlerts = computed(() =>
  (page.props.systemAlerts ?? []).filter(a => a.key !== 'deployment'),
)

// Server-rendered unread total across ALL database notification types.
const unreadNotificationsCount = computed(() => page.props.unreadNotificationsCount ?? 0)

const totalBellCount = computed(
  () => (bellAlerts.value?.length || 0) + (unreadNotificationsCount.value || 0),
)

const bellOverlay = ref(null)
const notifications = ref([])
const loading = ref(false)
const hasLoadedOnce = ref(false)

const hasUnread = computed(() => notifications.value.some(n => !n.read_at))

const fetchNotifications = async () => {
  loading.value = true
  try {
    const response = await fetch('/api/notifications', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
    const body = await response.json()
    notifications.value = body.data ?? []
    hasLoadedOnce.value = true
  } catch {
    notifications.value = []
  } finally {
    loading.value = false
  }
}

const toggleBell = event => {
  bellOverlay.value.toggle(event)
  // Fetch on every open so the list reflects freshly-arrived notifications.
  fetchNotifications()
}

// Matches the CSRF approach used by ReleaseNotesDialog.vue: read the
// XSRF-TOKEN cookie rather than the (refreshable) meta tag.
const csrfHeaders = () => ({
  Accept: 'application/json',
  'X-XSRF-TOKEN': decodeURIComponent(document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] ?? ''),
})

const refreshSharedCounts = () => {
  router.reload({ only: ['unreadNotificationsCount', 'unreadReleaseNotification'] })
}

const markAsRead = async notification => {
  if (notification.read_at) return
  try {
    await fetch(`/api/notifications/${notification.id}/read`, {
      method: 'POST',
      headers: csrfHeaders(),
      credentials: 'same-origin',
    })
    notification.read_at = new Date().toISOString()
    refreshSharedCounts()
  } catch {
    // leave it unread; the user can retry
  }
}

const markAllAsRead = async () => {
  try {
    await fetch('/api/notifications/read-all', {
      method: 'POST',
      headers: csrfHeaders(),
      credentials: 'same-origin',
    })
    const now = new Date().toISOString()
    notifications.value.forEach(n => {
      n.read_at = n.read_at ?? now
    })
    refreshSharedCounts()
  } catch {
    // no-op; retry on next interaction
  }
}

// Discriminate the database-notification payload into a renderable shape.
// Tolerant of pre-existing release rows that predate the `data.type` key.
const describe = notification => {
  const data = notification.data ?? {}

  if (data.type === 'invitation.accepted') {
    return {
      icon: 'pi pi-user-plus',
      title: t('messages.notificationInbox.types.invitationAccepted', {
        name: data.accepted_by_name ?? '',
        company: data.company_name ?? '',
      }),
      subtitle: data.role
        ? t('messages.notificationInbox.types.invitationAcceptedRole', {
            role: t(`messages.admin.role.${data.role}`, data.role),
          })
        : null,
    }
  }

  if (data.version) {
    return {
      icon: 'pi pi-sparkles',
      title: t('messages.notificationInbox.types.releasePublished', { version: data.version }),
      subtitle: data.summary ? String(data.summary).split('\n')[0].replace(/[#*]/g, '').trim() : null,
    }
  }

  return {
    icon: 'pi pi-bell',
    title: data.title ?? t('messages.notificationInbox.generic'),
    subtitle: null,
  }
}

const formatDate = iso => {
  if (!iso) return ''
  return new Date(iso).toLocaleString('et-EE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}
</script>

<template>
  <div class="relative">
    <div
      class="relative flex items-center gap-2 cursor-pointer bg-gray-700 rounded-md py-2 px-3 h-10 hover:bg-gray-800"
      @click="toggleBell"
    >
      <span class="pi pi-bell text-white" />
      <span
        v-if="totalBellCount"
        class="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full text-white text-xs w-5 h-5"
        :class="{ 'bg-yellow-300': bellAlerts.length, 'bg-primary': !bellAlerts.length && unreadNotificationsCount }"
      >
        <span v-if="bellAlerts.length" class="pi pi-exclamation-triangle text-yellow-700 mb-0.5 text-sm"></span>
        <span v-else>{{ unreadNotificationsCount }}</span>
      </span>
    </div>

    <Popover ref="bellOverlay" append-to="body">
      <div class="w-80 max-w-xs">
        <div class="flex items-center justify-between gap-2">
          <span class="font-semibold">{{ $t('messages.notifications') }}</span>
          <button
            v-if="hasUnread"
            type="button"
            class="text-xs text-primary hover:underline"
            @click="markAllAsRead"
          >
            {{ $t('messages.notificationInbox.markAllRead') }}
          </button>
        </div>

        <!-- System-health alerts: pinned at the top of the dialog so they
             stay visible while the notification list below scrolls. -->
        <div v-if="bellAlerts.length" class="space-y-2 mt-2">
          <div
            v-for="alert in bellAlerts"
            :key="`alert-${alert.key}`"
            class="flex items-center gap-2 p-2 bg-yellow-400/10 border border-yellow-400/20 rounded-md"
          >
            <span class="pi pi-exclamation-triangle text-yellow-500 mt-0.5" />
            <div class="text-sm">
              <div class="font-medium text-yellow-700">{{ alert.message }}</div>
              <div v-if="alert.affected_redata.length" class="text-xs text-yellow-700 pb-1">
                {{ $t('messages.affectedDownstream', { services: alert.affected_redata.join(', ') }) }}
              </div>
              <div class="text-xs text-gray-500">{{ $t('messages.since') }} {{ alert.since }}</div>
            </div>
          </div>
        </div>

        <!-- Scrollable database-notification list -->
        <div class="custom-scrollbar max-h-80 overflow-y-auto mt-2 pr-1">
          <div v-if="loading && !hasLoadedOnce" class="py-3 flex justify-center">
            <span class="pi pi-spin pi-spinner text-gray-400" />
          </div>

          <div v-else-if="notifications.length" class="flex flex-col">
            <button
              v-for="notification in notifications"
              :key="notification.id"
              type="button"
              class="flex items-start gap-2 p-2 text-left rounded-md hover:bg-gray-100 transition-colors"
              :class="{ 'opacity-60': notification.read_at }"
              @click="markAsRead(notification)"
            >
              <span
                class="mt-0.5"
                :class="[describe(notification).icon, notification.read_at ? 'text-gray-400' : 'text-primary']"
              />
              <span class="flex-1 min-w-0">
                <span class="block text-sm" :class="{ 'font-medium': !notification.read_at }">
                  {{ describe(notification).title }}
                </span>
                <span v-if="describe(notification).subtitle" class="block text-xs text-gray-500 truncate">
                  {{ describe(notification).subtitle }}
                </span>
                <span class="block text-xs text-gray-400">{{ formatDate(notification.created_at) }}</span>
              </span>
              <span
                v-if="!notification.read_at"
                class="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-primary"
                aria-hidden="true"
              />
            </button>
          </div>

          <div v-else-if="!bellAlerts.length" class="py-2 text-sm text-gray-500">
            {{ $t('messages.noNewNotifications') }}
          </div>
        </div>
      </div>
    </Popover>
  </div>
</template>

<style scoped>
  @reference '../../css/tailwind.css';

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--color-slate-200);
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: var(--color-slate-300);
}
</style>
